import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import type { RegionRepository } from '@/Domain/repositories/RegionRepository';
import type { StoreRepository } from '@/Domain/repositories/StoreRepository';
import type { AddressRepository } from '@/modules/address/domain/AddressRepository';
import type BuyNowRepository from '@/modules/buy-now/domain/BuyNowRepository';
import type { CartPaymentsRepository } from '@/modules/cart/domain/CartPaymentsRepository';
import type { CartRepository } from '@/modules/cart/domain/CartRepository';
import type { CartShippingMethodsRepository } from '@/modules/cart/domain/CartShippingMethodsRepository';
import type { CustomerRepository } from '@/modules/customer/domain/CustomerRepository';
import type { TokenUserIdRepository } from '@/modules/log/domain/TokenUserIdRepository';
import type { PickupRepository } from '@/modules/pickup/domain/PickupRepository';
import type { ShippingMethodRepository } from '@/modules/shippingMethod/domain/ShippingMethodRepository';

export interface Props extends PropsWithChildren {
  repositories: {
    regions: RegionRepository;
    addressRepository: AddressRepository;
    cart: CartRepository;
    cartPaymentsRepository: CartPaymentsRepository;
    cartShippingMethodsRepository: CartShippingMethodsRepository;
    stores: StoreRepository;
    pickupRepository: PickupRepository;
    customerRepository: CustomerRepository;
    shippingMethodsRepository: ShippingMethodRepository;
    tokenUserIdRepository: TokenUserIdRepository;
    buyNowRepository?: BuyNowRepository;
  };
}

const RepositoryContext = createContext<Props['repositories']>(
  {} as Props['repositories']
);

export const useRepository = () => useContext(RepositoryContext);

export const RepositoryProvider: FC<Props> = ({ children, repositories }) => {
  return (
    <RepositoryContext.Provider value={repositories}>
      {children}
    </RepositoryContext.Provider>
  );
};
